import { useState } from "react";

import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Button } from "react-bootstrap";
import { useRouter } from "next/router";

import { NewProjectDrawer } from "@bagel-web/common";
import { createProject } from "api";
import { NewProject } from "api/types";
import { FeatureFlagsProvider } from "utils/featureFlags";
import { OrganizationPermissions, useRBAC } from "utils/rbac";

/**
 * Full-page empty state shown on the Sessions and API Requests pages when the
 * authenticated user has no projects. There is nothing to display on those
 * pages until a project exists, so we direct the user to create their first
 * one. Creating a project here switches to it and returns to the originating
 * page, which then renders its real content.
 */
const NoProjectsZeroStateContent = () => {
  const [showDrawer, setShowDrawer] = useState(false);
  const router = useRouter();
  const queryClient = useQueryClient();
  const { hasPermission } = useRBAC();

  const canManageProjects = hasPermission(
    OrganizationPermissions.ManageProjects
  );

  const saveProjectMutation = useMutation({
    mutationFn: async (project: NewProject) => await createProject({ project }),
    onSuccess: (project) => {
      queryClient.invalidateQueries({ queryKey: ["projects"] });
      router.push(
        `/switch-project/${project.id}?path=${encodeURIComponent(
          router.asPath
        )}`
      );
    },
  });

  return (
    <div className="d-flex flex-column align-items-center justify-content-center text-center h-100">
      <i
        className="bi-folder-plus text-secondary"
        style={{ fontSize: "3rem" }}
      />
      <h2 className="mt-3 mb-3">Create your first Project</h2>
      {canManageProjects ? (
        <Button variant="primary" onClick={() => setShowDrawer(true)}>
          <i className="bi-plus-lg me-2" />
          Create Project
        </Button>
      ) : (
        <p className="text-muted">
          You do not have permission to create projects. Ask an organization
          administrator to create one for you.
        </p>
      )}
      <NewProjectDrawer
        show={showDrawer}
        onClose={() => setShowDrawer(false)}
        onSave={async (project) =>
          await saveProjectMutation.mutateAsync(project)
        }
      />
    </div>
  );
};

const NoProjectsZeroState = () => (
  <FeatureFlagsProvider>
    <NoProjectsZeroStateContent />
  </FeatureFlagsProvider>
);

export default NoProjectsZeroState;
